// Özel lig sayfaları (Junior Cup vb.) bağlamı.
//
// Etkin sayfalar açılışta bir kez okunur (/api/league-portals). Geçerli adres
//   * /<slug>/...                    -> portal adresi (isPortalRoute)
//   * /sehir/:il/lig/:lig/...         -> lig bir portala atanmışsa yine o portal
// olarak çözülür ve portalın teması <html data-portal="..."> ile uygulanır.
// Böylece maç, takım ve oyuncu ayrıntıları da aynı tasarımla görünür.
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiGet } from "../api/api";
import { buildPortalCss } from "./portalTheme";
import { findPortalByLeague, findPortalBySlug } from "./portalPaths";

export const PORTALS_ENDPOINT = "/api/league-portals";

const PortalContext = createContext({ portals: [], ready: true, portal: null, isPortalRoute: false });

// Yalnız sitenin kapsamlı adresleri (/sehir/:il/lig/:lig/...) tanınır; yönetim
// ekranlarındaki benzer adresler temayı almaz.
const leagueFromPath = (pathname) => {
  const parts = String(pathname || "").split("/").filter(Boolean);
  if (parts[0] !== "sehir") return "";
  const index = parts.indexOf("lig");
  return index >= 0 ? parts[index + 1] : "";
};

/** Adresten portalı çözer; testler ve bağlam aynı kuralı kullanır. */
export function resolvePortal(portals, pathname) {
  const first = String(pathname || "").split("/").filter(Boolean)[0] || "";
  const bySlug = findPortalBySlug(portals, first);
  if (bySlug) return { portal: bySlug, isPortalRoute: true };
  return { portal: findPortalByLeague(portals, leagueFromPath(pathname)), isPortalRoute: false };
}

const FONT_LINKS = {
  rounded: "https://fonts.googleapis.com/css2?family=Nunito:wght@500;700;800;900&display=swap",
};

export function PortalProvider({ children }) {
  const location = useLocation();
  const [state, setState] = useState({ portals: [], ready: false });

  useEffect(() => {
    let alive = true;
    apiGet(PORTALS_ENDPOINT, { cacheTtl: 5 * 60 * 1000 })
      .then((res) => {
        if (alive) setState({ portals: Array.isArray(res?.data?.portals) ? res.data.portals : [], ready: true });
      })
      .catch(() => {
        // Lig sayfası okunamazsa site her zamanki görünümüyle çalışır.
        if (alive) setState({ portals: [], ready: true });
      });
    return () => {
      alive = false;
    };
  }, []);

  const resolved = useMemo(() => resolvePortal(state.portals, location.pathname), [state.portals, location.pathname]);
  const { portal } = resolved;

  const css = useMemo(() => buildPortalCss(portal), [portal]);

  useEffect(() => {
    const root = document.documentElement;
    if (!portal) {
      delete root.dataset.portal;
      return undefined;
    }
    root.dataset.portal = portal.slug;
    const fontHref = FONT_LINKS[portal.layout?.fontStyle];
    if (fontHref && !document.querySelector(`link[href="${fontHref}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = fontHref;
      document.head.appendChild(link);
    }
    return () => {
      delete root.dataset.portal;
    };
  }, [portal]);

  const value = useMemo(
    () => ({ portals: state.portals, ready: state.ready, portal, isPortalRoute: resolved.isPortalRoute }),
    [state.portals, state.ready, portal, resolved.isPortalRoute]
  );

  return (
    <PortalContext.Provider value={value}>
      {css ? <style data-portal-theme={portal.slug}>{css}</style> : null}
      {children}
    </PortalContext.Provider>
  );
}

/** { portals, ready, portal, isPortalRoute } */
export const usePortal = () => useContext(PortalContext);
